import {
  Composite,
  CompositeGroup,
  CompositeGroupLabel,
  CompositeItem,
  CompositeProvider,
} from "@ariakit/react";
import React, {
  HTMLAttributes,
  ReactElement,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
} from "react";
import styled, { css } from "styled-components";

import { composeRefs } from "../hooks/useCompositeRef";
import { useId } from "../hooks/useUniqueId";
import { typedMemo } from "../util/typedMemo";

import { Flex } from "./Flex";
import { HexButton } from "./HexButton";
import { HexPopover, HexPopoverProps } from "./HexPopover";
import { HexTooltip } from "./HexTooltip";

// ----- Root -----

type RootProps<T extends string> = {
  panelId: T | null;
  onChangePanelId: (pId: T | null) => void;
  border?: "right" | "left" | "none";
} & HTMLAttributes<HTMLDivElement>;

const Root = typedMemo(function Root<T extends string>({
  border = "right",
  onChangePanelId,
  panelId,
  ...rest
}: RootProps<T>) {
  const ctx = useMemo(
    () => ({ panelId, onChangePanelId }),
    [panelId, onChangePanelId],
  ) as {
    panelId: string | null;
    onChangePanelId: (pId: string | null) => void;
  };
  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onChangePanelId(null);
      }
    },
    [onChangePanelId],
  );
  return (
    <SidebarCtx value={ctx}>
      <CompositeProvider focusLoop={true} orientation="vertical">
        <Composite
          render={
            <RootFlex
              $border={border}
              align="start"
              direction="column"
              justify="between"
            />
          }
          onKeyDown={onKeyDown}
          {...rest}
        />
      </CompositeProvider>
    </SidebarCtx>
  );
});

const RootFlex = styled(Flex)<{ $border: "right" | "left" | "none" }>`
  padding: 6px;
  ${({ $border }) =>
    $border === "right" &&
    css`
      border-right: 1px solid ${({ theme }) => theme.borderColor.MUTED};
    `}
  ${({ $border }) =>
    $border === "left" &&
    css`
      border-left: 1px solid ${({ theme }) => theme.borderColor.MUTED};
    `}
  align-self: stretch;
  height: 100%;
`;

// ----- Ctx -----

const SidebarCtx = createContext<{
  panelId: string | null;
  onChangePanelId: (pId: string | null) => void;
}>({
  panelId: null,
  onChangePanelId: () => {},
});

// ----- Section -----

type SectionProps = HTMLAttributes<HTMLDivElement>;
const Section = React.memo(function Section({
  children,
  ...rest
}: SectionProps) {
  return (
    <CompositeGroup
      render={
        <Flex align="start" direction="column" justify="start" {...rest}>
          {children}
        </Flex>
      }
    />
  );
});

const SectionLabel = CompositeGroupLabel;

// ----- Button -----

type ButtonProps = {
  panelId?: string;
  icon: ReactElement<any>;
  label: string;
  tooltip?: ReactNode;
  popover?: Omit<HexPopoverProps, "renderTarget" | "children">;
};
const Button = React.memo(function Button({
  icon,
  panelId,
  label,
  tooltip = label,
  popover,
}: ButtonProps) {
  const mockId = `$virtual-${useId()}`;
  const compositeId = panelId ?? mockId;

  const { onChangePanelId, panelId: currentPanelId } = useContext(SidebarCtx);
  const isCurrentPanel = compositeId === currentPanelId;

  return (
    <CompositeItem
      id={compositeId}
      // eslint-disable-next-line react/jsx-no-bind -- bind
      render={({ ref: compositeItemRef, ...compositeItemProps }) => (
        <HexPopover
          modifiers={{
            offset: { enabled: true, options: { offset: [0, 4] } },
            preventOverflow: { enabled: true, options: { padding: 8 } },
            flip: { enabled: true },
          }}
          shouldReturnFocusOnClose={true}
          {...popover}
          disabled={!popover}
          // eslint-disable-next-line react/jsx-no-bind -- bind
          renderTarget={({
            isOpen: isPopoverOpen,
            ref: popoverRef,
            tabIndex: ___,
            ...popoverProps
          }) => (
            <HexTooltip
              content={tooltip}
              disabled={isPopoverOpen}
              minimal={true}
              placement="right"
              // eslint-disable-next-line react/jsx-no-bind -- bind
              renderTarget={({
                isOpen: _,
                ref: tooltipRef,
                tabIndex: __,
                ...tooltipProps
              }) => (
                <HexButton
                  {...tooltipProps}
                  {...popoverProps}
                  {...compositeItemProps}
                  ref={composeRefs(popoverRef, tooltipRef, compositeItemRef)}
                  altFocusRing={true}
                  data-active-item={isCurrentPanel}
                  icon={icon}
                  intent={isCurrentPanel ? "primary" : "none"}
                  large={true}
                  minimal={!isCurrentPanel}
                  subtle={isCurrentPanel}
                />
              )}
            />
          )}
        />
      )}
      // eslint-disable-next-line react/jsx-no-bind -- bind
      onClick={
        panelId
          ? () => onChangePanelId(isCurrentPanel ? null : panelId)
          : undefined
      }
    />
  );
});

// ----- Export -----

export const HexSidebar = Object.freeze({
  Root,
  Section,
  SectionLabel,
  Button,
});
